import {
  ENV,
  Environment,
  mockServerPort,
} from '@backoffice-frontend/environment'
import type { ExpressionSpecification } from 'maplibre-gl'
import * as maplibregl from 'maplibre-gl'
import { FetchSource, PMTiles, Protocol } from 'pmtiles'

/**
 * Remote PMTiles host per environment.
 *
 * Adapted from admt-web-app's geonet/lanes pull. admt proxies tiles through an
 * Express backend that injects an `x-api-key`; backoffice is a static SPA with
 * no backend, so tiles are fetched directly and the API key is attached client
 * side via a custom `X-API-Key` header on the pmtiles FetchSource (below).
 *
 * The archive for a region is served at `${HOST}${regionId}`.
 */
const PMTilesStages: Record<Environment, string> = {
  [Environment.test]: `http://localhost:${mockServerPort}/api/pmtiles-apikey/`,
  [Environment.local]: `http://localhost:${mockServerPort}/api/pmtiles-apikey/`,
  [Environment.development]:
    'https://api.maps.dev.moia-group.io/pmtiles-apikey/',
  [Environment.integration]:
    'https://api.maps.int.moia-group.io/pmtiles-apikey/',
  [Environment.production]:
    'https://api.maps.prd.moia-group.io/pmtiles-apikey/',
  [Environment['production.us-east-1']]:
    'https://api.maps.prd.moia-group.io/pmtiles-apikey/',
}

/**
 * API key sent in the `X-API-Key` header, per environment.
 *
 * These keys are scoped to the browser-facing map-tiles API and are, by design,
 * shipped to the client (there is no backend to hold them) — the same way the
 * maps team's own pmtiles-demo attaches them. test/local hit the mock server and
 * need no key.
 */
const PMTilesApiKeyStages: Record<Environment, string> = {
  [Environment.test]: '',
  [Environment.local]: '',
  [Environment.development]: '39aoyMAvYkh0uYmJWUaV2fseIzhu0NR35sd3ldv6',
  [Environment.integration]: 'lsjlYhF6O99cNSBHXhnoX1ffrl8aGsZ18hlWr6T8',
  [Environment.production]: 'ObjHXWqZIfaOaajLZs8cz2DXgPmZ82P2aRIzvk7J',
  [Environment['production.us-east-1']]:
    'ObjHXWqZIfaOaajLZs8cz2DXgPmZ82P2aRIzvk7J',
}

const HOST = PMTilesStages[ENV]
const API_KEY = PMTilesApiKeyStages[ENV]

/**
 * Source-layer names inside a region's PMTiles archive.
 *
 * Lanes are drawn from the `segments` layer (the MOIA road graph) filtered to
 * AV-allowed edges — matching how admt-web-app renders lanes from these same
 * archives. Geonet is NOT streamed from PMTiles (admt always renders it from
 * local GeoJSON), so only `segments` is exposed here.
 */
export const PMTILES_SOURCE_LAYERS = {
  segments: 'segments',
} as const

export type PMTilesSourceLayer =
  (typeof PMTILES_SOURCE_LAYERS)[keyof typeof PMTILES_SOURCE_LAYERS]

/**
 * Only AV-allowed segments are lanes. Mirrors admt-web-app's lane filter.
 * Typed as `ExpressionSpecification` (not the broader `FilterSpecification`) so
 * it can be nested inside an `['all', ...]` combinator for the arrow layer.
 */
export const AV_ALLOWED_FILTER: ExpressionSpecification = [
  '==',
  ['get', 'autonomousVehiclesAllowed'],
  true,
]

/** Identifies a PMTiles-backed layer: which region archive, source-layer, and optional filter. */
export type PMTilesLayerRef = {
  /** Maps-API region id — the archive is served at `${HOST}${regionId}`. */
  regionId: string
  /** Source-layer within the archive, e.g. `geonet` or `segments`. */
  sourceLayer: PMTilesSourceLayer
  /** Optional feature filter (e.g. AV-allowed segments for lanes). */
  filter?: ExpressionSpecification
}

// Single protocol instance shared across all consumers.
let globalProtocol: Protocol | null = null
// One PMTiles instance per region, reused across re-renders and remounts so the
// internal tile cache is never discarded between toggle-off / toggle-on.
const pmTilesCache = new Map<string, PMTiles>()

const getPmTilesArchiveUrl = (regionId: string): string => `${HOST}${regionId}`

const apiKeyHeaders = (): Headers | undefined => {
  if (!API_KEY) return undefined
  const headers = new Headers()
  headers.set('X-API-Key', API_KEY)
  return headers
}

/**
 * Ensure the `pmtiles://` protocol is registered with MapLibre once and that the
 * given region has exactly one PMTiles instance (fetched with the API-key
 * header). Safe to call repeatedly.
 */
export const registerPMTilesArchive = (regionId: string): void => {
  if (!globalProtocol) {
    globalProtocol = new Protocol()
    maplibregl.addProtocol('pmtiles', globalProtocol.tile)
  }

  if (!pmTilesCache.has(regionId)) {
    const source = new FetchSource(
      getPmTilesArchiveUrl(regionId),
      apiKeyHeaders(),
    )
    const pmTiles = new PMTiles(source)
    globalProtocol.add(pmTiles)
    pmTilesCache.set(regionId, pmTiles)
  }
}

/** MapLibre vector-source URL for a region archive. */
export const pmtilesSourceUrl = (regionId: string): string =>
  `pmtiles://${getPmTilesArchiveUrl(regionId)}`
